import React from 'react';
import { Dispatch, SetStateAction, useRef, useState, useContext, useEffect } from "react";
import { TAttributeValue, TFetchAttributeValues, TFinalAttributeValue, TLoading } from "../types";
import ViewMoreButton from "Components/Common/components/ViewMoreButton";
import { AttributeContext } from "../AttributeContext";
import Utils from "../../../../Common/Utils";
import TextField from "Components/DesignSystem/TextField/TextField";
import Typography from "Components/DesignSystem/Typography/Typography";
import Tag from "Components/DesignSystem/Tag/Tag";
import ChoiceChip from "Components/DesignSystem/ChoiceChip/ChoiceChip";
import Search from "Components/DesignSystem/Search/Search";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

/**
 * Add/remove attribute values — on the design system since Phase 5. All value
 * bookkeeping (handleCreate's CSV parse + dedupe, handleDelete vs
 * cancelAddEditDelete, the deleted/not-deleted partition) is verbatim. Chips:
 * a saved value is a removable Tag, a value added this session a success-toned
 * Tag (legacy btn-outline-success), and a pending deletion a § 193
 * danger-toned ChoiceChip — clicking it cancels the deletion, as the legacy
 * danger chip did. The legacy CustomSearchBar's 1000 ms debounce now lives
 * here beside the DS Search it fed.
 */
type MyProps = {
    attValues: TFinalAttributeValue[],
    setAttValues: Dispatch<SetStateAction<TFinalAttributeValue[]>>,
    loading: TLoading,
    setLoading: Dispatch<SetStateAction<TLoading>>,
    finalAttributeValues: TFinalAttributeValue[]
    setFinalAttributeValues: Dispatch<SetStateAction<TFinalAttributeValue[]>>,
    fetchAttributeValues: TFetchAttributeValues,
    attValuesCount: number,
    search: string|undefined
    setSearch: Dispatch<SetStateAction<string|undefined>>
    /**
     * § 288 — a SEARCH fetch is in flight, as opposed to a view-more append. The
     * `loading` prop above cannot tell them apart: one status covers both.
     */
    searchLoading?: boolean
}

const SEARCH_DEBOUNCE_MS = 1000;

export default function EditAttValues ({
  attValues,
  loading,
  setLoading,
  finalAttributeValues,
  setFinalAttributeValues,
  fetchAttributeValues,
  attValuesCount,
  search,
  setSearch,
  searchLoading = false
}: MyProps) {
  const [textField, setTextfield] = useState("");
  const { attributeLoading } = useContext(AttributeContext);
  // DS Search is controlled and has no built-in debounce (the legacy
  // CustomSearchBar carried both); the raw text lives here and setSearch fires
  // after the same 1000 ms the legacy delay prop set.
  const [searchInput, setSearchInput] = useState(search ?? "");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);
  const handleSearchInput = (value: string) => {
    const trimmed = value.trimStart();
    setSearchInput(trimmed);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
  };

  function isEmpty(str: string) {
    function myTrim(x: string) {
      return x.replace(/^\s+|\s+$/gm, "");
    }

    return myTrim(str).length === 0;
  }

  function handleDelete(item: TFinalAttributeValue, index: number) {
    let obj = {
      ...item,
      edited: false,
      toggle: false,
      deleted: true,
      changed: true,
    };

    // will be a new value delete from finalAttributeValues
    // not other changes necessary
    setFinalAttributeValues((prev) => {
        const nextValue = [...prev]
        const index = finalAttributeValues.findIndex((value) => value.value === item.value)
        // if already existing item thats not deleted
        if(index === -1 && item.id) nextValue.push(obj)
        // if newly create item is deleted
        if(index !== -1 && !item.id) nextValue.splice(index, 1)

        return nextValue
    })
  }

  function cancelAddEditDelete(item: TFinalAttributeValue, index:number) {
    if(!finalAttributeValues) return
    setFinalAttributeValues((prev) => {
        const nextValues = [...prev]
        if(index !== -1) nextValues.splice(index, 1);
        return nextValues
    })
  }

  function findDuplicates(value: string) {
    let index = finalAttributeValues.findIndex((i) => i.value === value)
    let attIndex = attValues.findIndex((i) => i.value === value);

    return (index >= 0 || attIndex >= 0) ;
  }

  function handleCreate (e: React.KeyboardEvent<HTMLInputElement>) {
    let newValue = (e.target as HTMLInputElement).value;
    if (e.key === "Enter") {
        e.preventDefault();
        if (!isEmpty(newValue)) {
        let splitValue = Utils.parseCsvValues(newValue)
            .filter((e: string, i: number, a: string[]) => a.indexOf(e) === i);
        splitValue.forEach((item) => {
            let duplicates = findDuplicates(item);
            if (!duplicates) {
            let obj = {
                id: null,
                value: item,
                active: true,
                edited: false,
                deleted: false,
                toggle: false,
                changed: true,
            };
            setFinalAttributeValues((prev) => prev ? [...prev, obj] : [obj])
            setTextfield("");
            } else {
            setLoading({
                status: "Error",
                message:
                "Duplicate values cannot be added and are removed.",
            });
            setTextfield("");
            setTimeout(() => {
                setLoading({
                status: "",
                message: "",
                });
            }, 5000);
            }
        });
        }
    }
  }

  let deletedAttributes: TFinalAttributeValue[] = finalAttributeValues?.filter((item) => item.deleted) ?? [];
  const deletedAttributesValues = deletedAttributes.map((item) => item.value)
  // let notDeletedAttributes: TFinalAttributeValue[] = [...finalAttributeValues?.filter(item => !item.id), ...attValues.filter(item => !deletedAttributesValues.includes(item.value))] ?? []
  const notDeletedAttributes: TFinalAttributeValue[] = [
    ...(finalAttributeValues?.filter(item => !item.id) ?? []),
    ...attValues.filter(item => !deletedAttributesValues.includes(item.value))
  ];
  return (
    <div>
      <div>
        <div className="tw-p-4">
          <TextField
            label=""
            data-testid="attribute-edit-values-input"
            placeholder={"Type values and press 'ENTER' to add. Use quotes (\"...\") to preserve commas."}
            value={textField}
            onChange={(value) => {
              setTextfield(value);
            }}
            onKeyDown={handleCreate}
            id="AttributeValue"
            autoComplete="off"
          />
        </div>

        {/* Only the band HEADER is sunken (the twins' structure) — tinted chips
            on the sunken ground barely read (user-reported 2026-09-01). */}
        <div className="tw-border-y tw-border-edge">
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-bg-surface-sunken">
            <Typography variant="title-header" as="h3" className="tw-mb-0 tw-text-ink">
              Selected Attributes
            </Typography>
            <Search
              size="sm"
              className="tw-w-60"
              placeholder="Search Attribute Values"
              ariaLabel="Search attribute values"
              data-testid="attribute-values-search-input"
              value={searchInput}
              onChange={handleSearchInput}
              /* § 288 — the search-only flag, plus the 1000ms debounce in front of it.
                 The view-more append keeps its own spinner in the button's place. */
              loading={searchLoading || searchInput !== (search ?? "")}
              loadingLabel="Searching values"
              disabled={attributeLoading}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-p-4 tw-min-h-[50px]">
            {notDeletedAttributes.length > 0 &&
              notDeletedAttributes
              .filter((item) => !item.deleted)
              .map((item, index) => {
                return <Tag
                  key={index}
                  label={item.value}
                  /* A value added this session (id still null) is success-toned,
                     the legacy btn-outline-success. */
                  tone={item.id === null ? "success" : undefined}
                  data-testid={`attribute-value-chip-${item.id ?? item.value}`}
                  removeLabel="Remove value"
                  onRemove={() => {
                    if (
                      item.edited === true ||
                      item.deleted === true ||
                      item.id === null
                    ) {
                      cancelAddEditDelete(item, index);
                    } else {
                      handleDelete(item, index);
                    }
                  }}
                />
              })}

              {/* An append keeps the chips mounted; the fetch shows as an inline
                  spinner in the button's place (module-wide loading grammar,
                  user-requested 2026-09-01). */}
              {loading.status === "Loading" ? (
                <span
                  className="tw-inline-flex tw-items-center tw-px-2"
                  data-testid="attribute-values-loading-more"
                >
                  <Spinner size="sm" label="Loading more values" />
                </span>
              ) : (
                <ViewMoreButton
                  condition = {attValues.length < attValuesCount}
                  state = {attValues.length >= attValuesCount}
                  handleClick = {() => fetchAttributeValues(search, attValues.length, false)}
                />
              )}

          </div>

        </div>
        <div className="tw-p-4">
          <Typography variant="title-header" as="h3" className="tw-mb-3 tw-text-ink">
            Deleted Attributes
          </Typography>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-pb-3">
            {deletedAttributes.length > 0 &&
              deletedAttributes.map((item, index) => {
                return <ChoiceChip
                    key={index}
                    label={item.value}
                    selected={false}
                    /* § 193 danger tone: pending deletion; clicking it cancels
                       the deletion, as the legacy danger chip did. */
                    tone="danger"
                    data-testid={`attribute-value-deleted-${item.id ?? item.value}`}
                    onClick={() => {
                      if (
                        item.edited === true ||
                        item.deleted === true ||
                        item.id === null
                      ) {
                        cancelAddEditDelete(item, index);
                      } else {
                        handleDelete(item, index);
                      }
                    }}
                  />
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
